import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const DUMMY_OPPORTUNITIES = [
  { keyword: 'top keyword opportunity', pos: 6.5, impressions: 450, rec: 'Run a full report to see AI-generated recommendations for this client.' },
  { keyword: 'brand visibility search', pos: 8.2, impressions: 310, rec: 'Content depth and schema markup are common quick wins for this keyword type.' },
  { keyword: 'local search term', pos: 9.1, impressions: 220, rec: 'Adding structured data and a dedicated landing page typically improves position.' },
];

const DUMMY_FINDINGS = [
  'Report data is being processed — check back shortly for full insights.',
  'Schema markup and FAQ sections are common high-impact fixes.',
  'AI citation status will appear here once the audit is complete.',
];

const reportTypeColors = {
  'Full Report':             { background: 'var(--blush-light)',  color: 'var(--blush-dark)'  },
  'Gap Analysis':            { background: '#EEF2FF',             color: '#3730A3'             },
  'Competitor Intelligence': { background: '#FEF3C7',             color: '#92400E'             },
  'AI Visibility Audit':     { background: '#ECFDF5',             color: '#065F46'             },
  'Content Plan':            { background: '#FFF7ED',             color: '#7A5C1E'             },
};

const statusStyle = {
  Ready:      { background: '#ECFDF5', color: '#065F46' },
  Processing: { background: '#EEF2FF', color: '#3730A3' },
  Failed:     { background: '#FDECEA', color: '#B91C1C' },
};

const allTypes = ['All Types', 'Full Report', 'Gap Analysis', 'Competitor Intelligence', 'AI Visibility Audit', 'Content Plan'];
const allRanges = ['All Time', 'This Week', 'This Month', 'Last 30 Days'];

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} · ${time}`;
}

function isWithinRange(ts, range) {
  if (range === 'All Time' || !ts) return true;
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (range === 'Last 30 Days') return diff <= 30 * 86400000;
  if (range === 'This Month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (range === 'This Week') {
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    weekStart.setHours(0, 0, 0, 0);
    return d >= weekStart;
  }
  return true;
}

export default function Reports() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [rangeFilter, setRangeFilter] = useState('All Time');
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    async function fetchReports() {
      setLoading(true);
      setFetchError('');
      const { data, error } = await supabase
        .from('reports')
        .select('*, clients(business_name, domain)')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) setFetchError('Failed to load reports. Please refresh.');
      else setReports(data || []);
      setLoading(false);
    }
    fetchReports();
    return () => { cancelled = true; };
  }, [session]);

  const filtered = reports.filter(r => {
    const clientName = r.clients?.business_name ?? '';
    const domain = r.clients?.domain ?? '';
    const matchSearch = clientName.toLowerCase().includes(search.toLowerCase()) ||
                        domain.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === 'All Types' || r.report_type === typeFilter;
    const matchRange = isWithinRange(r.created_at, rangeFilter);
    return matchSearch && matchType && matchRange;
  });

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontFamily: 'var(--font-heading)',
          fontSize: '2.2rem',
          fontWeight: 600,
          color: 'var(--charcoal)',
          lineHeight: 1.15,
          marginBottom: '0.25rem',
        }}>
          Reports
        </h1>
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.875rem',
          color: 'var(--charcoal-muted)',
        }}>
          View and download all generated reports
        </p>
      </div>

      {/* Filter bar */}
      <div style={{
        display: 'flex',
        gap: '0.75rem',
        marginBottom: '1.75rem',
        flexWrap: 'wrap',
      }}>
        <input
          type="text"
          placeholder="Search by client name or domain…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            color: 'var(--charcoal)',
            background: 'var(--white)',
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.55rem 0.9rem',
            width: '260px',
            outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--blush-dark)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
        <FilterSelect value={typeFilter} onChange={setTypeFilter} options={allTypes} />
        <FilterSelect value={rangeFilter} onChange={setRangeFilter} options={allRanges} />
      </div>

      {/* Table */}
      <div style={{
        background: 'var(--white)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--off-white)' }}>
              {['Client', 'Report Type', 'Date Generated', 'Status', 'Actions'].map(col => (
                <th key={col} style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.09em',
                  textTransform: 'uppercase',
                  color: 'var(--charcoal-muted)',
                  padding: '0.7rem 1.1rem',
                  textAlign: col === 'Actions' ? 'right' : 'left',
                  borderBottom: '1px solid var(--border)',
                  whiteSpace: 'nowrap',
                }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} style={{
                  padding: '3rem',
                  textAlign: 'center',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--charcoal-muted)',
                }}>
                  Loading reports…
                </td>
              </tr>
            ) : fetchError ? (
              <tr>
                <td colSpan={5} style={{
                  padding: '3rem',
                  textAlign: 'center',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: '#B91C1C',
                }}>
                  {fetchError}
                </td>
              </tr>
            ) : reports.length === 0 ? (
              <tr>
                <td colSpan={5} style={{
                  padding: '3.5rem',
                  textAlign: 'center',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--charcoal-muted)',
                }}>
                  No reports yet — run your first report to get started.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={5} style={{
                  padding: '3rem',
                  textAlign: 'center',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  color: 'var(--charcoal-muted)',
                }}>
                  No reports match your filters.
                </td>
              </tr>
            ) : filtered.map((r, i) => (
              <tr
                key={r.id}
                style={{ transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--off-white)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{
                  padding: '0.9rem 1.1rem',
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: 'var(--charcoal)',
                    marginBottom: '0.1rem',
                  }}>
                    {r.clients?.business_name ?? '—'}
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.75rem',
                    color: 'var(--blush-dark)',
                  }}>
                    {r.clients?.domain ?? ''}
                  </div>
                </td>
                <td style={{
                  padding: '0.9rem 1.1rem',
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <span style={{
                    ...(reportTypeColors[r.report_type] ?? { background: 'var(--off-white)', color: 'var(--charcoal-muted)' }),
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    padding: '0.2rem 0.7rem',
                    borderRadius: '999px',
                    whiteSpace: 'nowrap',
                  }}>
                    {r.report_type}
                  </span>
                </td>
                <td style={{
                  padding: '0.9rem 1.1rem',
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.83rem',
                  color: 'var(--charcoal-muted)',
                  whiteSpace: 'nowrap',
                }}>
                  {formatDate(r.created_at)}
                </td>
                <td style={{
                  padding: '0.9rem 1.1rem',
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <span style={{
                    ...(statusStyle[r.status] ?? { background: 'var(--off-white)', color: 'var(--charcoal-muted)' }),
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    padding: '0.2rem 0.7rem',
                    borderRadius: '999px',
                  }}>
                    {r.status}
                  </span>
                </td>
                <td style={{
                  padding: '0.9rem 1.1rem',
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                  textAlign: 'right',
                }}>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <ActionBtn
                      label="View"
                      onClick={() => r.status === 'Ready' && setViewing(r)}
                      disabled={r.status !== 'Ready'}
                      primary
                    />
                    <ActionBtn label="PDF" disabled={r.status !== 'Ready'} />
                    <ActionBtn label="Re-run" onClick={() => navigate('/run')} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewing && (
        <ReportModal report={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

function ReportModal({ report, onClose }) {
  const gap        = report.gap_analysis     ?? null;
  const competitor = report.competitor_intel ?? null;
  const ai         = report.ai_visibility    ?? null;

  console.log('[ReportModal] gap_analysis raw:', gap);
  console.log('[ReportModal] ai_visibility raw:', ai);

  // DataForSEO response is stored directly as the JSONB value.
  // run-gap-analysis returns: { keywords_found, opportunities, findings, raw_keywords, domain, date_range_days }
  // run-ai-visibility returns: { citations_found, citation_score, results, opportunities, findings, keywords_checked, domain }
  // Fallback paths cover nested summary objects in case the shape varies.
  const summary = {
    keywords:      gap?.keywords_found
                ?? gap?.summary?.keywords_found
                ?? gap?.summary?.keywords
                ?? (gap?.raw_keywords?.length != null ? String(gap.raw_keywords.length) : '—'),
    competitors:   competitor?.total_analyzed
                ?? competitor?.summary?.competitors
                ?? '—',
    citations:     ai?.citations_found
                ?? ai?.summary?.citations_found
                ?? ai?.summary?.citations
                ?? '—',
    citationScore: ai?.citation_score
                ?? ai?.summary?.citation_score
                ?? ai?.summary?.citationScore
                ?? '—',
  };

  const opportunities = gap?.opportunities ?? competitor?.opportunities ?? ai?.opportunities ?? DUMMY_OPPORTUNITIES;
  const findings      = gap?.findings      ?? competitor?.findings      ?? ai?.findings      ?? DUMMY_FINDINGS;

  const scoreColor = summary.citationScore === 'PRESENT' ? '#065F46'
                   : summary.citationScore === 'PARTIAL'  ? '#92400E'
                   : summary.citationScore === '—'        ? 'var(--charcoal-muted)'
                   : '#B91C1C';

  function generatePDF() {
    const clientName  = report.clients?.business_name ?? '—';
    const domain      = report.clients?.domain        ?? '';
    const reportDate  = formatDate(report.created_at);
    const reportType  = report.report_type            ?? '';

    const oppsHTML = opportunities.map((opp, i) => `
      <div class="opp-card">
        <div class="opp-header">
          <span class="opp-num">${i + 1}</span>
          <span class="opp-keyword">${escHtml(opp.keyword ?? '')}</span>
        </div>
        <div class="opp-meta">
          ${opp.pos        ? `Position ${opp.pos}` : ''}
          ${opp.pos && opp.impressions ? ' &nbsp;·&nbsp; ' : ''}
          ${opp.impressions ? `${Number(opp.impressions).toLocaleString()} searches/mo` : ''}
        </div>
        ${opp.rec ? `<div class="opp-rec">${escHtml((opp.rec ?? '').split('\n\n')[0])}</div>` : ''}
        ${opp.matched_url
          ? `<div class="opp-page"><strong>Page to update:</strong> ${escHtml(opp.matched_url)}</div>`
          : `<div class="opp-page opp-new">No matching page found — create a new page targeting "${escHtml(opp.keyword ?? '')}".</div>`
        }
        ${opp.matched_title
          ? `<div class="opp-page"><strong>Current title:</strong> ${escHtml(opp.matched_title)}</div>`
          : ''
        }
      </div>
    `).join('');

    const findingsHTML = findings.map(f =>
      `<li>${escHtml(String(f))}</li>`
    ).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${escHtml(clientName)} — SEO Report</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&family=Inter:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Inter', Arial, sans-serif;
    font-size: 11pt;
    color: #2d2d2d;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ── Header ── */
  .header {
    background: #F2A8B8;
    padding: 22px 32px 18px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }
  .header-left h1 {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 20pt;
    font-weight: 600;
    color: #2d2d2d;
    line-height: 1.2;
  }
  .header-left .powered {
    font-size: 8pt;
    color: #7a3d4d;
    margin-top: 4px;
  }
  .header-right {
    text-align: right;
    font-size: 8.5pt;
    color: #2d2d2d;
  }
  .header-right .report-type {
    font-weight: 600;
    font-size: 9pt;
  }

  /* ── Client info ── */
  .client-info {
    padding: 18px 32px 12px;
    border-bottom: 1px solid #F2A8B8;
  }
  .client-name {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 17pt;
    font-weight: 600;
    color: #2d2d2d;
  }
  .client-domain {
    font-size: 9pt;
    color: #F2A8B8;
    margin-top: 3px;
  }

  /* ── Section ── */
  .section {
    padding: 18px 32px;
    border-bottom: 1px solid #ece8e4;
  }
  .section-title {
    font-size: 7.5pt;
    font-weight: 600;
    letter-spacing: 0.1em;
    color: #999;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  /* ── Stats grid ── */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }
  .stat-card {
    background: #faf8f6;
    border-radius: 6px;
    padding: 10px 8px;
    text-align: center;
  }
  .stat-value {
    font-size: 18pt;
    font-weight: 600;
    color: #2d2d2d;
    line-height: 1;
  }
  .stat-label {
    font-size: 6.5pt;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #999;
    margin-top: 5px;
  }

  /* ── Opportunity cards ── */
  .opp-card {
    padding: 12px 0;
    border-bottom: 1px solid #f3eeea;
    page-break-inside: avoid;
  }
  .opp-card:last-child { border-bottom: none; }
  .opp-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 5px;
  }
  .opp-num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    min-width: 20px;
    border-radius: 50%;
    background: #F2A8B8;
    font-size: 8pt;
    font-weight: 600;
    color: #2d2d2d;
  }
  .opp-keyword {
    font-size: 11pt;
    font-weight: 600;
    color: #2d2d2d;
  }
  .opp-meta {
    font-size: 8pt;
    color: #999;
    margin-left: 30px;
    margin-bottom: 6px;
  }
  .opp-rec {
    font-size: 9pt;
    color: #555;
    line-height: 1.5;
    margin-left: 30px;
    margin-bottom: 5px;
  }
  .opp-page {
    font-size: 8pt;
    color: #888;
    margin-left: 30px;
    margin-top: 3px;
    word-break: break-all;
  }
  .opp-new { font-style: italic; }

  /* ── Findings ── */
  .findings-list {
    list-style: none;
    padding: 0;
  }
  .findings-list li {
    font-size: 9.5pt;
    color: #444;
    line-height: 1.6;
    padding: 5px 0 5px 18px;
    position: relative;
    border-bottom: 1px solid #f3eeea;
    page-break-inside: avoid;
  }
  .findings-list li:last-child { border-bottom: none; }
  .findings-list li::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 12px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #C9A84C;
  }

  /* ── Footer ── */
  .footer {
    padding: 14px 32px;
    background: #faf8f6;
    border-top: 2px solid #F2A8B8;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 7.5pt;
    color: #aaa;
  }

  /* ── Print rules ── */
  @media print {
    @page {
      size: A4;
      margin: 0;
    }
    body { margin: 0; }
    .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .stat-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .opp-num { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .findings-list li::before { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .footer { position: fixed; bottom: 0; left: 0; right: 0; }
  }
</style>
</head>
<body>

<div class="header">
  <div class="header-left">
    <h1>SEO &amp; GEO Intelligence Report</h1>
    <div class="powered">Powered by Pivot &amp; Prosper · pivotandprospermarketing.com</div>
  </div>
  <div class="header-right">
    <div class="report-type">${escHtml(reportType)}</div>
    <div>${escHtml(reportDate)}</div>
  </div>
</div>

<div class="client-info">
  <div class="client-name">${escHtml(clientName)}</div>
  <div class="client-domain">${escHtml(domain)}</div>
</div>

<div class="section">
  <div class="section-title">Summary</div>
  <div class="stats-grid">
    <div class="stat-card">
      <div class="stat-value">${escHtml(String(summary.keywords))}</div>
      <div class="stat-label">Keywords Found</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">${escHtml(String(summary.competitors))}</div>
      <div class="stat-label">Competitors Analysed</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">${escHtml(String(summary.citations))}</div>
      <div class="stat-label">Citations Found</div>
    </div>
    <div class="stat-card">
      <div class="stat-value">${escHtml(String(summary.citationScore))}</div>
      <div class="stat-label">Citation Score</div>
    </div>
  </div>
</div>

<div class="section">
  <div class="section-title">Top Opportunities</div>
  ${opportunities.length === 0
    ? '<p style="font-style:italic;color:#aaa;font-size:9pt;">No opportunities data available.</p>'
    : oppsHTML
  }
</div>

<div class="section">
  <div class="section-title">Key Findings</div>
  ${findings.length === 0
    ? '<p style="font-style:italic;color:#aaa;font-size:9pt;">No findings data available.</p>'
    : `<ul class="findings-list">${findingsHTML}</ul>`
  }
</div>

<div class="footer">
  <span>Generated by Pivot &amp; Prosper · pivotandprospermarketing.com</span>
  <span>${escHtml(reportDate)}</span>
</div>

</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) {
      alert('Pop-up blocked — please allow pop-ups for this site and try again.');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    // Small delay lets fonts and layout settle before print dialog opens
    setTimeout(() => { win.print(); }, 600);
  }

  function escHtml(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }


  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(45,45,45,0.45)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '580px',
          maxWidth: '95vw',
          height: '100%',
          background: 'var(--white)',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-lg)',
          animation: 'slideIn 0.22s ease',
          overflow: 'hidden',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '1.75rem 2rem 1.25rem',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, marginRight: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                <span style={{
                  ...(reportTypeColors[report.report_type] ?? { background: 'var(--off-white)', color: 'var(--charcoal-muted)' }),
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.68rem',
                  fontWeight: 500,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '0.2rem 0.65rem',
                  borderRadius: '999px',
                }}>
                  {report.report_type}
                </span>
                <span style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  color: 'var(--charcoal-muted)',
                }}>
                  {formatDate(report.created_at)}
                </span>
              </div>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '1.65rem',
                fontWeight: 600,
                color: 'var(--charcoal)',
                lineHeight: 1.2,
                marginBottom: '0.15rem',
              }}>
                {report.clients?.business_name ?? '—'}
              </h2>
              <p style={{
                fontFamily: 'var(--font-body)',
                fontSize: '0.8rem',
                color: 'var(--blush-dark)',
              }}>
                {report.clients?.domain ?? ''}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none',
                fontSize: '1.2rem', color: 'var(--charcoal-muted)',
                cursor: 'pointer', flexShrink: 0, padding: '0.2rem',
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.75rem 2rem' }}>

          {/* Summary stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '0.75rem',
            marginBottom: '2rem',
          }}>
            {[
              { label: 'Keywords Found',       value: summary.keywords      },
              { label: 'Competitors Analysed', value: summary.competitors   },
              { label: 'Citations Found',       value: summary.citations     },
              { label: 'Citation Score',        value: summary.citationScore, highlight: true },
            ].map((s, i) => (
              <div key={i} style={{
                background: 'var(--off-white)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.9rem 0.85rem',
                textAlign: 'center',
              }}>
                <div style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: s.highlight ? '1rem' : '1.5rem',
                  fontWeight: 600,
                  color: s.highlight ? scoreColor : 'var(--charcoal)',
                  lineHeight: 1.1,
                  marginBottom: '0.25rem',
                }}>
                  {s.value}
                </div>
                <div style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.65rem',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--charcoal-muted)',
                }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Top Opportunities */}
          <Section title="Top Opportunities">
            {opportunities.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--charcoal-muted)' }}>
                No opportunity data available yet.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {opportunities.map((opp, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    gap: '0.85rem',
                    padding: '0.9rem 1rem',
                    background: 'var(--off-white)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                  }}>
                    <div style={{
                      width: '22px',
                      height: '22px',
                      background: 'var(--blush)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'var(--font-heading)',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: 'var(--charcoal)',
                      flexShrink: 0,
                      marginTop: '1px',
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                        <span style={{
                          fontFamily: 'var(--font-body)',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          color: 'var(--charcoal)',
                          fontStyle: 'italic',
                        }}>
                          "{opp.keyword}"
                        </span>
                        {(opp.pos != null || opp.impressions != null) && (
                          <span style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: '0.72rem',
                            color: 'var(--charcoal-muted)',
                          }}>
                            {opp.pos != null ? `pos ${opp.pos}` : ''}
                            {opp.pos != null && opp.impressions != null ? ' · ' : ''}
                            {opp.impressions != null ? `${Number(opp.impressions).toLocaleString()} impr.` : ''}
                          </span>
                        )}
                      </div>
                      <p style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.8rem',
                        color: 'var(--charcoal-light)',
                        lineHeight: 1.5,
                      }}>
                        {opp.rec ?? opp.recommendation ?? ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Key Findings */}
          <Section title="Key Findings">
            {findings.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--charcoal-muted)' }}>
                No findings available yet.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {findings.map((f, i) => (
                  <li key={i} style={{ display: 'flex', gap: '0.65rem', alignItems: 'flex-start' }}>
                    <span style={{
                      color: 'var(--gold)',
                      fontSize: '0.8rem',
                      marginTop: '3px',
                      flexShrink: 0,
                    }}>◈</span>
                    <span style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.85rem',
                      color: 'var(--charcoal-light)',
                      lineHeight: 1.55,
                    }}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        {/* Footer */}
        <div style={{
          padding: '1.25rem 2rem',
          borderTop: '1px solid var(--border)',
          display: 'flex',
          gap: '0.75rem',
          flexShrink: 0,
          background: 'var(--off-white)',
        }}>
          <button style={{
            flex: 1,
            fontFamily: 'var(--font-body)',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: 'var(--white)',
            background: 'var(--gold)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            padding: '0.7rem 1rem',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            onClick={generatePDF}
          >
            ↓ Download PDF
          </button>
          <button
            onClick={onClose}
            style={{
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
              fontSize: '0.875rem',
              color: 'var(--charcoal)',
              background: 'transparent',
              border: '1.5px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.7rem 1.25rem',
              cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blush-dark)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            Close
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1.75rem' }}>
      <h4 style={{
        fontFamily: 'var(--font-heading)',
        fontSize: '1.05rem',
        fontWeight: 600,
        color: 'var(--charcoal)',
        marginBottom: '0.85rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid var(--border)',
      }}>
        {title}
      </h4>
      {children}
    </div>
  );
}

function ActionBtn({ label, onClick, disabled, primary }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.75rem',
        fontWeight: 500,
        color: primary && !disabled ? 'var(--charcoal)' : 'var(--charcoal-muted)',
        background: primary && !disabled ? 'var(--blush-light)' : 'transparent',
        border: `1.5px solid ${primary && !disabled ? 'var(--blush-dark)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-sm)',
        padding: '0.3rem 0.7rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.875rem',
        color: 'var(--charcoal)',
        background: 'var(--white)',
        border: '1.5px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        padding: '0.55rem 2rem 0.55rem 0.85rem',
        outline: 'none',
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237A7A7A' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 0.75rem center',
      }}
      onFocus={e => e.target.style.borderColor = 'var(--blush-dark)'}
      onBlur={e => e.target.style.borderColor = 'var(--border)'}
    >
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
}
